import { ShortCode } from '@highereducation/wordpress';
import {
  Button,
  ButtonProps,
  LucideIconName,
  Flex,
} from '@highereducation/hen-ui';
import {
  type HenMetadataAttrs,
  parseTruthy,
  pickHenMetadataAttrs,
} from '../utils';

const UNDERLINES = ['hover', 'always'] as const satisfies readonly NonNullable<
  ButtonProps['underline']
>[];

const HOVER_TREATMENTS = [
  'solid',
  'gradient',
] as const satisfies readonly NonNullable<ButtonProps['hoverTreatment']>[];

const pickOption = <T extends string>(
  options: readonly T[],
  value: unknown,
): T | undefined => (options.includes(value as T) ? (value as T) : undefined);

interface ButtonShortcodeProps
  extends Omit<
      ButtonProps,
      'disabled' | 'underline' | 'hoverTreatment' | 'inverse'
    >,
    HenMetadataAttrs {
  /**
   * Role names: `primary`, `secondary`, `outlined`, `text`.
   *
   * Deprecated aliases that render identically: `primaryFill` (use
   * `primary`), `blackFill` (use `secondary`), `blackOutline` (use
   * `outlined`) and `textOnlyNeutral` (use `text`). Stored shortcodes keep
   * working with them.
   */
  variant?:
    | 'primary'
    | 'secondary'
    | 'outlined'
    | 'text'
    | 'primaryOutline'
    | 'grayOutline'
    | 'textOnly'
    | 'primaryFill'
    | 'blackFill'
    | 'blackOutline'
    | 'textOnlyNeutral';
  size?: 'xxs' | 'xs' | 'sm' | 'lg';
  /** `hover` or `always`; text styles only. */
  underline?: string;
  /** `solid` or `gradient`; `gradient` applies to `primary` only. */
  'hover-treatment'?: string;
  /** `"true"` switches `secondary` to Secondary Inverse. */
  inverse?: string;
  href?: string;
  'icon-before'?: LucideIconName;
  'icon-after'?: LucideIconName;
  disabled?: boolean;
  align?: 'start' | 'center' | 'end';
  children?: React.ReactNode;
  [key: string]: unknown;
}

const alignToJustify = {
  start: 'start',
  center: 'center',
  end: 'end',
} as const;

export const ButtonShortcode: ShortCode<ButtonShortcodeProps> = {
  component: ({
    variant = 'primary',
    size = 'sm',
    href,
    target,
    rel,
    'data-hen-location': dataHenLocation,
    'data-hen-name': dataHenName,
    'icon-before': iconBefore,
    'icon-after': iconAfter,
    disabled,
    underline,
    'hover-treatment': hoverTreatment,
    inverse,
    align,
    width,
    'width-md': widthMd,
    'width-lg': widthLg,
    children,
  }) => {
    const metadataAttrs = pickHenMetadataAttrs({
      'data-hen-location': dataHenLocation,
      'data-hen-name': dataHenName,
    });

    const underlineOption = pickOption(UNDERLINES, underline);
    const hoverTreatmentOption = pickOption(HOVER_TREATMENTS, hoverTreatment);
    // Unset or unrecognized options pass nothing, so Button keeps its defaults.
    const buttonOptions = {
      ...(underlineOption && { underline: underlineOption }),
      ...(hoverTreatmentOption && { hoverTreatment: hoverTreatmentOption }),
      ...(parseTruthy(inverse) && { inverse: true }),
    };

    const button = href ? (
      <Button
        href={href}
        target={target}
        rel={target === '_blank' ? 'noopener noreferrer' : rel}
        variant={variant}
        size={size}
        iconBefore={iconBefore}
        iconAfter={iconAfter}
        disabled={disabled}
        width={width}
        width-md={widthMd}
        width-lg={widthLg}
        {...buttonOptions}
        {...metadataAttrs}
      >
        {children}
      </Button>
    ) : (
      <Button
        variant={variant}
        size={size}
        iconBefore={iconBefore}
        iconAfter={iconAfter}
        disabled={disabled}
        width={width}
        width-md={widthMd}
        width-lg={widthLg}
        {...buttonOptions}
        {...metadataAttrs}
      >
        {children}
      </Button>
    );

    if (align) {
      return (
        <Flex direction='row' justify={alignToJustify[align]}>
          {button}
        </Flex>
      );
    }

    return button;
  },
};
